import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { catchError, map, Observable, of } from 'rxjs';
import { UserDataResponse } from '../interfaces/user-data-response.interface';
import { UserDataDetailResponse } from '../interfaces/user-data-detail-response.interface';
import { UserDataDetailUpdateRequest } from '../interfaces/user-data-detail-update-request.interface';
import { ModalService } from './modal.service';
import { UserDataUpdateRequest } from '../../profile/interfaces/user-data-update-request.interface';
import { UserCedisUpdateRequest } from '../../profile/interfaces/user-cedis-update-request.interface';
import { UserRecognitionsDiplomasResponse } from '../interfaces/user-recognitions-diplomas-response.interface';
import { GetUserLevels } from '../interfaces/get-user-levels.interface';
import { UserPointsHistoryResponse } from '../interfaces/user-points-history-response.interface';
import { ActionCategoryResponse } from '../interfaces/action-category-response.interface';
import { GetUserActionResponse } from '../interfaces/get-user-action-response.interface';
import { cleanEmptyParams } from '@utilities/functions/clean-empty-params';
import { FilterRequest } from '../interfaces/filter-request.interface';
import { SearchUserResponse } from '../interfaces/search-user-response.interface';

const API_URL = environment.apiUrl;

@Injectable({ providedIn: 'root' })
export class UserService {
  private http = inject(HttpClient);
  private modalService = inject(ModalService);

  #userUrl = `${API_URL}/users`;

  getUserData(): Observable<UserDataResponse> {
    return this.http.get<UserDataResponse>(`${this.#userUrl}/getUserData`);
  }

  getUserDataDetail(): Observable<UserDataDetailResponse> {
    return this.http.get<UserDataDetailResponse>(
      `${this.#userUrl}/getUserDataDetail`,
    );
  }

  updateUseDataDetail(
    request: UserDataDetailUpdateRequest,
  ): Observable<boolean> {
    return this.http.post(`${this.#userUrl}/update/dataDetail`, request).pipe(
      map(() => true),
      catchError((err: HttpErrorResponse) => {
        this.modalService.showErrorModal(err);
        return of(false);
      }),
    );
  }

  updateUserData(request: UserDataUpdateRequest): Observable<boolean> {
    return this.http.post(`${this.#userUrl}/update/data`, request).pipe(
      map(() => true),
      catchError((err: HttpErrorResponse) => {
        this.modalService.showErrorModal(err);
        return of(false);
      }),
    );
  }

  updateUserPhoto(request: FormData): Observable<boolean> {
    return this.http.post(`${this.#userUrl}/photo/update`, request).pipe(
      map(() => true),
      catchError((err: HttpErrorResponse) => {
        this.modalService.showErrorModal(err);
        return of(false);
      }),
    );
  }

  updateUserCedis(request: UserCedisUpdateRequest): Observable<boolean> {
    return this.http.post(`${this.#userUrl}/update/cedis`, request).pipe(
      map(() => true),
      catchError((err: HttpErrorResponse) => {
        this.modalService.showErrorModal(err);
        return of(false);
      }),
    );
  }

  getUserLevels(): Observable<GetUserLevels[]> {
    return this.http.get<GetUserLevels[]>(`${this.#userUrl}/getLevels`);
  }

  getUserHistoryPointsByCatContestId(
    catContestId: string,
  ): Observable<UserPointsHistoryResponse[]> {
    return this.http.get<UserPointsHistoryResponse[]>(
      `${this.#userUrl}/history/points/${catContestId}`,
    );
  }

  getUserActionsCategories(): Observable<ActionCategoryResponse[]> {
    return this.http.get<ActionCategoryResponse[]>(
      `${this.#userUrl}/actions/categories`,
    );
  }

  getUserActions(actionType: number): Observable<GetUserActionResponse[]> {
    const params = new HttpParams().set('actionType', actionType);
    return this.http.get<GetUserActionResponse[]>(
      `${this.#userUrl}/userActions`,
      { params },
    );
  }

  postUserAction(request: FormData): Observable<boolean> {
    return this.http.post(`${this.#userUrl}/userAction`, request).pipe(
      map(() => true),
      catchError((err: HttpErrorResponse) => {
        this.modalService.showErrorModal(err);
        return of(false);
      }),
    );
  }

  getSearchUsers(filter: FilterRequest): Observable<SearchUserResponse[]> {
    const params = cleanEmptyParams(filter);
    return this.http.get<SearchUserResponse[]>(`${this.#userUrl}`, {
      params,
    });
  }
}
